import { activityLogger } from '@databases/app-access';
import React, {
  createContext,
  useState,
  PropsWithChildren,
  FC,
  useCallback,
} from 'react';

import { ConfirmDialogProps } from '../types';

type AllowedDialogTypes = {
  DevtoolsDialog: undefined;
  ConfirmDialog: ConfirmDialogProps;
  ExportDialog: undefined;
  ImportDialog: undefined;
};

type AllowedDialogKeys = keyof AllowedDialogTypes;
type DialogDataType = AllowedDialogTypes[AllowedDialogKeys];

export type DialogsType = {
  [key in AllowedDialogKeys]?: React.ReactNode;
};
type ShowDialogType = <key extends AllowedDialogKeys>(
  dialog: key,
  data?: AllowedDialogTypes[key]
) => void;

export interface DialogContextInterface {
  showDialog: ShowDialogType;
  hideDialog: () => void;
  getData: <T extends AllowedDialogKeys>() => AllowedDialogTypes[T];
}

export const DialogContext = createContext<DialogContextInterface>({
  showDialog: () => {},
  hideDialog: () => {},
  getData: <T,>() => null as T,
});

export const DialogProvider: FC<
  PropsWithChildren<{
    defaultDialogs: DialogsType;
  }>
> = ({ children, defaultDialogs }) => {
  const [dialogs] = useState<DialogsType>(defaultDialogs);
  const [dialog, setDialog] = useState<{
    component: React.ReactNode;
    data: DialogDataType;
  }>({ component: null, data: undefined });

  const showDialog: ShowDialogType = useCallback(
    (dialog, data) => {
      setDialog({
        component: dialogs[dialog],
        data: data as Extract<typeof data, DialogDataType>,
      });
      activityLogger.log('showDialog', { dialog, ...data });
    },
    [dialogs]
  );

  const hideDialog = useCallback(() => {
    setDialog({ component: null, data: undefined });
    activityLogger.log('hideDialog', { dialog });
  }, [dialog]);

  const getData = useCallback(<T,>(): T => dialog.data as T, [dialog.data]);

  return (
    <DialogContext.Provider
      value={{
        showDialog,
        hideDialog,
        getData,
      }}
    >
      {children}
      {dialog.component}
    </DialogContext.Provider>
  );
};
